<template>
  <FullPage class="org-projects" :class="{ repairing: needsRepair }">
    <div v-if="!needsRepair" class="projects-header">
      <BaseNumberedHeader title="Projects" :number="projectsCount" />
      <BaseButton
        v-if="currentWorkspace && currentDatabase && canCreate"
        class="new-project-button"
        kind="main"
        type="primary"
        text="New"
        :state="isCreatingProject ? 'loading' : 'default'"
        @click="newProject"
      >
        <template #icon>
          <PlusIcon />
        </template>
      </BaseButton>
    </div>

    <ProvisionedRepair
      v-if="needsRepair"
      class="repair"
      :org-id="orgId"
      :repairing="isRepairing"
      :failed="repairFailed"
      @repair="repair"
    />
    <template v-else>
      <div
        v-if="currentWorkspace && workspaceOptions.length > 1"
        class="toolbar"
      >
        <BaseInputSelect
          :key="`workspace-select-${selectRefresh}`"
          class="workspace-select"
          :value="currentWorkspace.id"
          :options="workspaceOptions"
          select-only
          @selected="selectWorkspace($event.value)"
        >
          <template #item="{ option }">
            <BaseInputSelectOptionItem :title="option.label">
              <template v-if="!isWorkspaceAccessible(option.value)" #right>
                <LockIcon class="lock-icon" />
              </template>
            </BaseInputSelectOptionItem>
          </template>
        </BaseInputSelect>
      </div>

      <div v-if="isLoading" class="loader">
        <BaseSkeletonList is-compact :size="10" />
      </div>
      <div
        v-else-if="currentWorkspace && currentDatabase && projects.length > 0"
        class="project-list"
      >
        <div
          v-for="project in projects"
          :key="project.id"
          class="project-row"
          @click="openProject(project.id, $event)"
          @contextmenu.prevent.stop="showProjectMenu(project.id, $event)"
        >
          <div class="project-main">
            <ProjectIcon class="project-icon" />
            <span class="project-title">{{ project.title }}</span>
          </div>
          <div class="project-progress">
            <div
              class="project-progress-bar"
              role="progressbar"
              :aria-valuenow="project.progressPercent"
              :aria-valuemin="0"
              :aria-valuemax="100"
              :aria-label="`${project.closedCount} of ${project.totalCount} tasks closed`"
            >
              <div
                class="project-progress-fill"
                :style="{ width: `${project.progressPercent}%` }"
              />
            </div>
            <span class="project-counts">
              {{ project.closedCount }} / {{ project.totalCount }}
            </span>
          </div>
        </div>
      </div>
      <div v-else class="empty">
        <span v-if="currentWorkspace">
          Create a project to organize this workspace's tasks.
        </span>
        <span v-else>This organization has no workspace.</span>
      </div>
    </template>
  </FullPage>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";

import controllerSingleton from "@ctrl/controller";
import type {
  GraphDatabase,
  GraphQuery,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseInputSelectOptionItem from "@/components/base/BaseInputSelectOptionItem.vue";
import BaseNumberedHeader from "@/components/base/BaseNumberedHeader.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import {
  builtinTasksDatabase,
  TASK_ASSIGNEE_COLUMN,
} from "@/components/graph/provisioned";
import { getGraphQuery, runCompiledQuery } from "@/components/graph/query";
import {
  asArray,
  asCountNumber,
  asGraphRecordResult,
  asMap,
  asString,
} from "@/components/graph/result";
import {
  displayTitle,
  extractTitleFromGraphResult,
} from "@/components/graph/title";
import {
  extractNotesFields,
  getTableObjectData,
  saveNotesField,
} from "@/components/graph/utils";
import LockIcon from "@/components/icons/LockIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import ProjectIcon from "@/components/icons/ProjectIcon.vue";
import * as inject from "@/components/inject";
import { createObject } from "@/components/modals/object";
import { openUpgradeModal } from "@/components/modals/upgrade";
import {
  buildProjectsListQuery,
  buildProjectTaskCountsQuery,
  type ProjectTasksSource,
} from "@/components/projects";
import ProvisionedRepair from "@/components/ProvisionedRepair.vue";
import { definitiveTableId, TASKS_TABLE_ID } from "@/components/tables";
import FullPage from "@/components/templates/FullPage.vue";
import { getDefaultViewConfigForDataSource } from "@/components/views/config/default";
import {
  organizationWorkspaceSelectionStorageKey,
  resolveExistingProjectsDatabase,
  resolveExistingTasksDatabase,
  WORKSPACES_ENTITLEMENT_KEY,
} from "@/components/workspaces";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { isWorkspaceWritable } from "@/composables/useProfile";
import { useProvisionedRepair } from "@/composables/useProvisionedRepair";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import type { CustomObjectTarget } from "@/types";
import { navigateToId } from "@/utils/navigate";

type ProjectRow = {
  id: string;
  title: string;
  closedCount: number;
  totalCount: number;
  progressPercent: number;
};

const closedSharePercent = (
  closedCount: number,
  totalCount: number
): number => {
  if (totalCount <= 0) return 0;
  return Math.max(
    0,
    Math.min(100, Math.round((closedCount / totalCount) * 100))
  );
};

export default defineComponent({
  name: "Projects",
  components: {
    BaseButton,
    BaseInputSelect,
    BaseInputSelectOptionItem,
    BaseNumberedHeader,
    BaseSkeletonList,
    FullPage,
    LockIcon,
    PlusIcon,
    ProjectIcon,
    ProvisionedRepair,
  },
  props: {
    orgId: { type: String, required: true },
  },
  async setup(props) {
    const releaser = useReleaser();
    const controller = inject.controller();
    const baseLogger = inject.logger();
    const profile = inject.profile();
    const billing = inject.billing();
    const router = inject.router();
    const workspaceDatabases = inject.workspaceDatabases();
    const contextMenu = inject.contextMenu();
    const personalDefaultTypes = inject.personalDefaultTypes();

    const organization = computed(() =>
      profile.value.organizations.find((org) => org.id === props.orgId)
    );
    const workspaces = computed(() => organization.value?.workspaces ?? []);
    const defaultWorkspaceId = computed(
      () =>
        workspaces.value.find((workspace) => workspace.default)?.id ??
        workspaces.value[0]?.id
    );
    const isEntitledToWorkspaces = computed<boolean>(
      () =>
        billing.value?.isEntitledTo(WORKSPACES_ENTITLEMENT_KEY, props.orgId)
          ?.value ?? false
    );
    const isWorkspaceAccessible = (workspaceId: string): boolean =>
      isEntitledToWorkspaces.value || workspaceId === defaultWorkspaceId.value;
    const accessibleWorkspaces = computed(() =>
      workspaces.value.filter((workspace) =>
        isWorkspaceAccessible(workspace.id)
      )
    );

    const { get, set, syncState } = useBrowserStore(
      profile.value.user.accountId
    );
    const selectedWorkspaceId = releaser.add(
      syncState<string>(
        organizationWorkspaceSelectionStorageKey(props.orgId),
        get(
          `projects:org:${props.orgId}:workspace`,
          defaultWorkspaceId.value ?? ""
        )
      )
    );
    // A prior selection of a now-locked workspace must not block the screen:
    // fall back to the default without opening the upgrade modal.
    if (
      selectedWorkspaceId.value &&
      !isWorkspaceAccessible(selectedWorkspaceId.value) &&
      defaultWorkspaceId.value
    ) {
      selectedWorkspaceId.value = defaultWorkspaceId.value;
    }
    const currentWorkspace = computed(
      () =>
        accessibleWorkspaces.value.find(
          (workspace) => workspace.id === selectedWorkspaceId.value
        ) ?? accessibleWorkspaces.value[0]
    );
    const workspaceOptions = computed<SelectOption[]>(() => {
      const defaultId = defaultWorkspaceId.value;
      return [...workspaces.value]
        .sort((a, b) => {
          if (a.id === defaultId) return -1;
          if (b.id === defaultId) return 1;
          return a.name.localeCompare(b.name);
        })
        .map((workspace) => ({
          value: workspace.id,
          label: workspace.name,
          aliases: [],
        }));
    });
    const selectRefresh = ref(0);
    const selectWorkspace = (workspaceId: string) => {
      if (!isWorkspaceAccessible(workspaceId)) {
        openUpgradeModal(billing, props.orgId, WORKSPACES_ENTITLEMENT_KEY);
        selectRefresh.value += 1;
        return;
      }
      selectedWorkspaceId.value = workspaceId;
    };

    const currentDatabase = computed<GraphDatabase | undefined>(() => {
      const workspaceId = currentWorkspace.value?.id;
      if (!workspaceId) return undefined;
      return resolveExistingProjectsDatabase(
        workspaceDatabases.value?.[workspaceId] ?? {}
      );
    });

    const currentTasksDatabase = computed<GraphDatabase | undefined>(() => {
      const workspaceId = currentWorkspace.value?.id;
      if (!workspaceId) return undefined;
      const databases = workspaceDatabases.value?.[workspaceId] ?? {};
      const isPersonalWorkspace =
        workspaceId === profile.value.user.personal.workspaceId;
      return isPersonalWorkspace
        ? builtinTasksDatabase(databases)
        : resolveExistingTasksDatabase(databases);
    });

    // Inspect the provisioned types of all the organization's workspaces:
    // when any is broken the whole screen becomes a Repair empty state,
    // even if the displayed workspace is unaffected. `currentDatabase`
    // re-resolves reactively when a repaired type lands.
    const { needsRepair, isRepairing, repairFailed, repair } =
      useProvisionedRepair(controller, profile, () =>
        workspaces.value.map((workspace) => workspace.id)
      );

    const isLoading = ref(true);
    const listQueryRef = ref<GraphQuery | null>(null);
    watch(
      currentDatabase,
      async (database) => {
        isLoading.value = true;
        listQueryRef.value = null;
        if (!database) {
          isLoading.value = false;
          return;
        }
        try {
          const query = await getGraphQuery(
            database.workspace,
            buildProjectsListQuery(database.id),
            controller
          );
          listQueryRef.value = query ?? null;
        } finally {
          isLoading.value = false;
        }
      },
      { immediate: true }
    );

    const listResult = await releaser.add(
      bindAsync(listQueryRef, async (query) => {
        if (!query) return releasableRef(undefined);
        return await runCompiledQuery(query, controller);
      })
    );

    const projectsBase = computed<Array<Pick<ProjectRow, "id" | "title">>>(
      () => {
        const map = asMap(listResult.value ?? null);
        if (!map) return [];
        return Array.from(map.entries()).flatMap(([key, value]) => {
          const record = value ? asGraphRecordResult(value) : undefined;
          const routine = record
            ? asGraphRecordResult(record["_routine"])
            : undefined;
          const id =
            asString(key) ?? (routine ? asString(routine["id"]) : undefined);
          if (!id || !record) return [];
          return [
            {
              id,
              title: displayTitle(extractTitleFromGraphResult(value)),
            },
          ];
        });
      }
    );

    // String source so renames that keep the same ids don't recompile counts.
    // Builtin personal tasks must be queried as `tasks` (virtual Status);
    // org Task types keep a real Status column on databases.["id"].rows.
    const countsQueryString = computed(() => {
      const tasksDb = currentTasksDatabase.value;
      if (!tasksDb || projectsBase.value.length === 0) return null;
      const source: ProjectTasksSource =
        definitiveTableId(tasksDb.id) === TASKS_TABLE_ID
          ? { kind: "builtin" }
          : { kind: "database", id: tasksDb.id };
      return buildProjectTaskCountsQuery(
        source,
        projectsBase.value.map((project) => project.id)
      );
    });

    const countsResult = await releaser.add(
      bindAsync(countsQueryString, async (queryString) => {
        const workspaceId = currentWorkspace.value?.id;
        if (!queryString || !workspaceId) return releasableRef(undefined);
        const query = await getGraphQuery(workspaceId, queryString, controller);
        if (!query) return releasableRef(undefined);
        return await runCompiledQuery(query, controller);
      })
    );

    const countFor = (projectIndex: number, cellIndex: number): number => {
      const rows = asArray(countsResult.value ?? null);
      const row = rows?.[projectIndex];
      const cells = row === undefined ? undefined : asArray(row);
      const cell = cells?.[cellIndex];
      return cell === undefined ? 0 : (asCountNumber(cell) ?? 0);
    };

    const projects = computed<ProjectRow[]>(() =>
      projectsBase.value.map((project, index) => {
        const closedCount = countFor(index, 0);
        const totalCount = countFor(index, 1);
        return {
          ...project,
          closedCount,
          totalCount,
          progressPercent: closedSharePercent(closedCount, totalCount),
        };
      })
    );
    const projectsCount = computed(() => projects.value.length);

    // Seed a fresh project's notes with a task board: a query block over the
    // workspace's Tasks database, filtered to this project and grouped by
    // Status. The board layout is not part of the query — it is a per-user
    // display preference the Query node view reads from the browser store
    // under `blocks/<id>:layout` — so it is preset before the block lands.
    const seedProjectNotes = async (
      workspaceId: WorkspaceId,
      database: GraphDatabase,
      projectRowId: string
    ) => {
      // The personal workspace has no provisioned Task type: its tasks are
      // the hardcoded built-in tasks table (whose "Project" relation the
      // Repair gate keeps intact). Its virtual "Status" column exposes the
      // same Todo/Completed/Discarded values as the provisioned type, so
      // the same filter and grouping apply — it just has no "Assignee".
      const isPersonalWorkspace =
        workspaceId === profile.value.user.personal.workspaceId;
      const databases = workspaceDatabases.value?.[workspaceId] ?? {};
      const tasksDatabase = isPersonalWorkspace
        ? builtinTasksDatabase(databases)
        : resolveExistingTasksDatabase(databases);
      if (!tasksDatabase) return;

      const config = await getDefaultViewConfigForDataSource(
        controller,
        workspaceId,
        tasksDatabase
      );
      const query = await controller.graph.view.toQuery({
        ...config,
        // Board cards only show the task title and its assignee (org
        // workspaces only: personal tasks have no Assignee column).
        // Grouping is applied before the visible-fields selection in the
        // compiled query (see `to_graph` in the controller's view params),
        // so the hidden Status column still drives the board columns.
        visible_fields: isPersonalWorkspace
          ? ["Title"]
          : ["Title", TASK_ASSIGNEE_COLUMN],
        filter: {
          type: "atom",
          atom: {
            lhs: "Project",
            type: "relation",
            op: "eq",
            rhs: projectRowId,
          },
        },
        groupings: [{ column_name: "Status" }],
      });

      const objectId = await controller.graph.object.id.make({
        workspace_id: workspaceId,
        table_id: database.id,
        row_id: projectRowId,
      });
      const result = (await controller.graph.object.get(objectId)).release();
      const data = getTableObjectData(result);
      if (!data) return;
      const notesField = extractNotesFields(data, database).find(
        (field) => field.propertyTitle === "Notes"
      );
      if (!notesField) return;

      // No group order is seeded: the View falls back to the provisioned
      // Task Status defaults (see `selectConfigOfField` in provisioned.ts),
      // which every member and device resolves the same way.
      const blockId = controllerSingleton.makeBlockId();
      set(`blocks/${blockId}:layout`, "board");
      set(`blocks/${blockId}:isWide`, true);

      await saveNotesField(
        workspaceId,
        { tableId: database.id, rowId: projectRowId },
        {
          propertyTitle: notesField.propertyTitle,
          newValue: notesField.serialize([
            ...notesField.blocks,
            { id: blockId, type: "query", content: query },
          ]),
        },
        controller
      );
    };

    // A `reader` member of the displayed workspace cannot create projects:
    // the header's "New" button is hidden.
    const canCreate = computed(() =>
      isWorkspaceWritable(profile.value, currentWorkspace.value?.id)
    );

    const isCreatingProject = ref(false);
    const newProject = async () => {
      const workspace = currentWorkspace.value;
      if (!workspace || isCreatingProject.value || !canCreate.value) return;

      // Creation never provisions: without a matching Projects type the
      // screen shows the Repair empty state instead of this button.
      const database = currentDatabase.value;
      if (!database) return;

      isCreatingProject.value = true;
      const modal = createObject(baseLogger);
      try {
        const result = await modal.openObjectModal(
          database.id,
          workspace.id,
          controller,
          profile
        );
        if (result?.objectId) {
          // Seeding failures must not break the creation flow (the project
          // exists either way): the notes simply stay as the user left them.
          try {
            await seedProjectNotes(workspace.id, database, result.objectId);
          } catch (error) {
            window.console.error("Failed to seed the project's notes", error);
          }
        }
        if (result?.shouldRedirect && result.objectId) {
          await navigateToId(
            router,
            controller,
            personalDefaultTypes.value || {},
            workspace.id,
            database.id,
            result.objectId
          );
        }
      } finally {
        modal.release();
        isCreatingProject.value = false;
      }
    };

    const openProject = async (projectId: string, event?: MouseEvent) => {
      const workspace = currentWorkspace.value;
      const database = currentDatabase.value;
      if (!workspace || !database) return;
      await navigateToId(
        router,
        controller,
        personalDefaultTypes.value || {},
        workspace.id,
        database.id,
        projectId,
        event
      );
    };

    const showProjectMenu = (projectId: string, event: MouseEvent) => {
      const workspace = currentWorkspace.value;
      const database = currentDatabase.value;
      if (!workspace || !database) return;
      const target: CustomObjectTarget = {
        type: "object",
        workspace_id: workspace.id,
        table_id: database.id,
        row_id: projectId,
      };
      contextMenu.value.show({ type: "object", value: target }, event);
    };

    return {
      canCreate,
      currentDatabase,
      currentWorkspace,
      isCreatingProject,
      isLoading,
      isRepairing,
      isWorkspaceAccessible,
      needsRepair,
      newProject,
      openProject,
      projects,
      projectsCount,
      repair,
      repairFailed,
      selectRefresh,
      selectWorkspace,
      showProjectMenu,
      workspaceOptions,
    };
  },
});
</script>

<style scoped lang="scss">
.org-projects {
  // Sizing for the projects list. Skipped for the repair empty state,
  // which must fill FullPage's default content box: with border-box sizing
  // the 64px paddings eat into `height: calc(100% - 96px)`, and the message
  // would center ~64px higher than on the Tasks and Pages screens.
  &:not(.repairing) :deep(.content) {
    width: 100%;
    padding: 64px;
    box-sizing: border-box;
  }

  .projects-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .base-numbered-header {
      flex: 1;
      min-width: 0;
    }

    .new-project-button {
      flex-shrink: 0;
    }
  }
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 16px;

  .workspace-select {
    width: 220px;

    .lock-icon {
      width: 12px;
      height: 12px;
      flex-shrink: 0;
      :deep(.stroke) {
        stroke: $red;
      }
    }
  }
}

.loader {
  padding-top: 8px;
}

.project-list {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.project-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 24px;
  // Same inset on both sides so the icon and the counts sit equally
  // inside the hover background (avoid shorthand asymmetry).
  padding-block: 8px;
  padding-inline: 16px;
  border-radius: $border-radius-m;
  cursor: default;
  box-sizing: border-box;
  @include transition(background-color 120ms ease-out);

  &:hover {
    @include themed() {
      background-color: t($grey-05);
    }
  }
}

.project-main {
  display: flex;
  align-items: center;
  column-gap: 10px;
  min-width: 0;
}

.project-icon {
  display: block;
  width: 14px;
  height: 14px;
  flex-shrink: 0;

  :deep(.stroke) {
    @include themed() {
      stroke: t($grey-50);
    }
  }
}

.project-title {
  @extend %font-body-regular;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @include themed() {
    color: t($grey-90);
  }
}

.project-progress {
  display: flex;
  align-items: center;
  column-gap: 8px;
  flex-shrink: 0;
}

.project-progress-bar {
  display: flex;
  width: 64px;
  height: 4px;
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;

  @include themed() {
    background-color: t($grey-10);
  }
}

.project-progress-fill {
  height: 100%;

  @include themed() {
    background-color: t($primary);
  }
}

.project-counts {
  @extend %font-caption-regular;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;

  @include themed() {
    color: t($grey-50);
  }
}

.repair {
  flex: 1;
  min-height: 240px;
}

.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;

  span {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-50);
    }
  }
}
</style>
